import { type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import LinkIcon from '@atlaskit/icon/core/link';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { Tooltip } from '@trello/nachos/tooltip';
import { useIsInboxBoard } from '@trello/personal-workspace';
import type { CardBackTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import * as styles from './ActionCopyLink.module.less';

interface ActionCopyLinkProps {
  isCopied: boolean;
  onCopyLink: () => void;
  type: 'action' | 'comment';
}

export const ActionCopyLink: FunctionComponent<ActionCopyLinkProps> = ({
  isCopied,
  onCopyLink,
  type,
}) => {
  const isInboxCard = useIsInboxBoard();
  if (isInboxCard) {
    return null;
  }

  return (
    <div className={styles.copyCommentWrapper}>
      <Tooltip
        content={
          isCopied ? (
            <FormattedMessage
              id="templates.action.copied"
              defaultMessage="Copied!"
              description="Tooltip for when the user copies a link to the comment or action"
            />
          ) : (
            <FormattedMessage
              id="templates.action.copy-link-to"
              defaultMessage="Copy link to {type}"
              description="Tooltip telling the user to copy the link to the comment or action"
              values={{ type }}
            />
          )
        }
        position="auto"
      >
        <Button
          appearance="subtle"
          className={styles.copyLinkButton}
          data-testid={getTestId<CardBackTestIds>(
            'card-back-action-copy-button',
          )}
          onClick={onCopyLink}
          aria-label={intl.formatMessage(
            {
              id: 'templates.action.copy-link-to',
              defaultMessage: 'Copy link to {type}',
              description:
                'Tooltip telling the user to copy the link to the comment or action',
            },
            { type },
          )}
        >
          {isCopied ? (
            <CheckCircleIcon
              label={intl.formatMessage({
                id: 'templates.action.copied',
                defaultMessage: 'Copied!',
                description:
                  'Tooltip for when the user copies a link to the comment or action',
              })}
              color={token('color.icon.success', '#4bce97')}
              size="small"
            />
          ) : (
            <LinkIcon
              label={intl.formatMessage(
                {
                  id: 'templates.action.copy-link-to',
                  defaultMessage: 'Copy link to {type}',
                  description:
                    'Tooltip telling the user to copy the link to the comment or action',
                },
                { type },
              )}
              color={token('color.text.subtle', '#44546f')}
              size="small"
            />
          )}
        </Button>
      </Tooltip>
    </div>
  );
};
